import {
  ICollision,
  CollideHandler,
} from '../../actions/collisions/ICollision';
import { InvalidEffect } from '../../actions/effects/InvalidEffect';
import { CollideEvent, CollideId } from '../../defs/collide';
import { despawnBlockEntity } from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import { OctopusBlockEntity } from './OctopusBlockEntity';
import { blockIterateAll } from '../../util/blockTools';
import { OctopusBlockEffect } from './OctopusBlockEffect';

export class OctopusBlockCollision implements ICollision {
  private readonly scene: GameScene;
  private readonly entity: OctopusBlockEntity;
  private readonly resolvers: { [key in CollideId]?: CollideHandler } = {
    tap: this.resolveTap,
    block: this.resolveBlock,
    attack: this.resolveAttack,
  };

  constructor(scene: GameScene, entity: OctopusBlockEntity) {
    this.scene = scene;
    this.entity = entity;
  }

  public async resolve(event: CollideEvent): Promise<boolean> {
    return this.resolvers[event.id]?.call(this, event) ?? false;
  }

  private async resolveTap() {
    await new InvalidEffect(this.entity).execute();
    return false;
  }

  private async resolveBlock(event: CollideEvent) {
    this.attack();
    return true;
  }

  private async resolveAttack(event: CollideEvent) {
    this.attack();
    return true;
  }

  private async attack() {
    const entity = this.entity;
    const component = entity.c.blockOctopus;

    // Hit the octopus and poke the scene
    component.count += 1;
    this.scene?.events.publish({ id: 'poke' });
    this.scene?.sessionSystem.triggerGoal('octopus', entity);

    const cleared = this.clearOctopusGroup();
    // If the group is cleared, do not spawn bubbles
    if (cleared) return;

    // Play hit animation and spawn bubble
    entity.c.blockOctopus.view.playHopAnimation();
    const effect = new OctopusBlockEffect(this.scene, { entity });
    await effect.spawnBubble();
  }

  private clearOctopusGroup() {
    // Number of hits required in the group to clear all octopuses
    // Fallback to 0 but all octopuses levels will have octopus goal
    const goal = this.scene?.sessionEntity.c.map.goals.octopus ?? 0;

    // If goal not completed yet, do not clear octopuses yet.
    if (goal >= 1) return false;

    // The group is all octopuses in the level
    const group: OctopusBlockEntity[] = [];

    // Gather all octopuses in the level
    blockIterateAll(this.scene, (entity) => {
      if (entity.c.block.blockId === 'octopus') {
        group.push(entity as OctopusBlockEntity);
      }
    });

    // Clear the entire octopus group
    group.forEach((octopus) => {
      octopus.c.blockOctopus.count = 0;
      const effect = new OctopusBlockEffect(this.scene, { entity: octopus });
      effect.playClearAnimation();
      despawnBlockEntity(this.scene, octopus);
    });

    // The group has been cleared
    return true;
  }
}
